import { useMemo, useState } from "react";
import { AlertTriangle, Check, ExternalLink, Lightbulb, X } from "lucide-react";
import { cn } from "../../../utils/tailwindUtils";

/**
 * The finished research report: summary tiles and six views (trustedge
 * research/services.report). Presentation only; the panel owns fetching and
 * the review call. Every fact shows the page it came from, and the words on
 * that page, next to the value.
 */

export const VIEWS = ["Overview", "Profile", "Contacts", "Sales insights", "Updates", "Sources", "History"];

const STATUS_TONE = {
    match: "text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-300",
    partial: "text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300",
    no_match: "text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300",
    unknown: "text-gray-500 bg-gray-100 dark:bg-gray-700/50 dark:text-gray-300",
};
const STATUS_LABEL = { match: "Match", partial: "Partly", no_match: "No match", unknown: "Unknown" };

function Section({ title, children, aside }) {
    return (
        <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{title}</h4>
                {aside}
            </div>
            {children}
        </section>
    );
}

function Tile({ label, value, hint }) {
    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
            {hint && <p className="text-[11px] text-gray-500 dark:text-gray-400">{hint}</p>}
        </div>
    );
}

export function SummaryTiles({ job }) {
    const fit = job.fit || {};
    return (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Tile label="Company fit"
                value={job.fit_score == null ? "—" : `${job.fit_score}/100`}
                hint={fit.configured === false ? "Set the ideal customer in Settings"
                    : job.fit_score == null ? "Nothing could be judged"
                    : `Judged on ${fit.judged}% of criteria`} />
            <Tile label="Identity" value={job.identity_confidence == null ? "—" : `${job.identity_confidence}%`}
                hint={job.identity_confidence === 100 ? "Website confirmed by a person" : "Match with the CRM record"} />
            <Tile label="Evidence coverage" value={job.evidence_coverage == null ? "—" : `${job.evidence_coverage}%`}
                hint="Core profile found with a source" />
            <Tile label="Sources reviewed" value={job.sources_count}
                hint={job.cost?.ai_calls ? `${job.cost.ai_calls} AI call${job.cost.ai_calls === 1 ? "" : "s"}` : null} />
        </div>
    );
}

/** One value with its evidence: the source chip opens the page, the excerpt says why. */
function Evidence({ v }) {
    const [open, setOpen] = useState(false);
    return (
        <span className="inline-flex flex-col">
            <span className="inline-flex flex-wrap items-center gap-1.5">
                <span className={cn("text-xs text-gray-900 dark:text-gray-100", v.status === "conflict" && "text-amber-700 dark:text-amber-300")}>
                    {v.value}
                </span>
                {v.source_url && (
                    <a href={v.source_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1 text-[10px] text-gray-600 hover:text-primary dark:bg-gray-700 dark:text-gray-300"
                        title={v.source_url}>
                        {v.source_id || "source"} <ExternalLink className="h-2.5 w-2.5" aria-hidden="true" />
                    </a>
                )}
                {v.excerpt && (
                    <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
                        className="text-[10px] text-primary hover:underline">{open ? "hide" : "why"}</button>
                )}
            </span>
            {open && <q className="mt-0.5 max-w-prose text-[11px] italic text-gray-500 dark:text-gray-400">{v.excerpt}</q>}
        </span>
    );
}

function FieldRows({ fields, showUnknown = true }) {
    const rows = showUnknown ? fields : fields.filter(f => f.values.length);
    if (!rows.length) return <p className="text-xs text-gray-400">Nothing found.</p>;
    return (
        <dl className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {rows.map(f => (
                <div key={f.key} className="grid grid-cols-1 gap-1 py-1.5 sm:grid-cols-[180px_minmax(0,1fr)]">
                    <dt className="text-xs text-gray-500 dark:text-gray-400">
                        {f.label}
                        {f.values.some(v => v.status === "conflict") && (
                            <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                                <AlertTriangle className="h-3 w-3" aria-hidden="true" /> sources disagree
                            </span>
                        )}
                    </dt>
                    <dd className="flex flex-col gap-1">
                        {f.values.length ? f.values.map((v, i) => <Evidence key={i} v={v} />)
                            : <span className="text-xs text-gray-400">Unknown</span>}
                    </dd>
                </div>
            ))}
        </dl>
    );
}

function Overview({ job }) {
    const ins = job.insights;
    const fit = job.fit || {};
    const basic = job.groups.find(g => g.key === "basic");
    // Online booking, patient portal, reports online, vendors seen: things we
    // may also sell, so a rep sees them before the pitch (not only in Profile).
    const online = job.groups.flatMap(g => g.fields)
        .filter(f => ["digital_services", "current_software"].includes(f.key));
    return (
        <div className="space-y-5">
            {ins?.summary ? <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-100">{ins.summary}</p>
                : <p className="text-xs text-gray-500">No AI summary for this run{job.depth === "quick" ? " (Quick depth)" : ""}. The verified facts are below.</p>}
            {ins?.next_action?.action && (
                <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-3 dark:border-brand-800 dark:bg-brand-900/20">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Recommended next step</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{ins.next_action.action}</p>
                    {ins.next_action.why && <p className="text-xs text-gray-500 dark:text-gray-400">{ins.next_action.why}</p>}
                </div>
            )}
            {online.some(f => f.values.length) && (
                <Section title="Already on their website">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        What patients can already do online, and any software seen on the site. Ask what runs it before pitching ours.
                    </p>
                    <FieldRows fields={online} showUnknown={false} />
                </Section>
            )}
            {fit.criteria?.length > 0 && (
                <Section title="Why this fit score">
                    <ul className="space-y-1">
                        {fit.criteria.map(c => (
                            <li key={c.key} className="flex flex-wrap items-center gap-2 text-xs">
                                <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-semibold", STATUS_TONE[c.status])}>
                                    {STATUS_LABEL[c.status]}
                                </span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">{c.label}</span>
                                <span className="text-gray-500 dark:text-gray-400">{c.detail}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        Fit compares the profile with your ideal customer. It is not buying intent. Unknown criteria are left out, not counted against them.
                        {ins?.fit_explanation ? ` ${ins.fit_explanation}` : ""}
                    </p>
                </Section>
            )}
            {ins?.business && Object.values(ins.business).some(Boolean) && (
                <Section title="Business understanding">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[140px_minmax(0,1fr)]">
                        {[["what", "What they do"], ["customers", "Who they serve"], ["services", "Services"],
                          ["scale", "Scale"], ["notes", "Notes"]].filter(([k]) => ins.business[k]).map(([k, label]) => (
                            <div key={k} className="contents">
                                <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
                                <dd className="text-xs text-gray-800 dark:text-gray-100">{ins.business[k]}</dd>
                            </div>
                        ))}
                    </dl>
                </Section>
            )}
            {basic && <Section title="Basic information"><FieldRows fields={basic.fields} /></Section>}
            {job.notes?.length > 0 && (
                <Section title="What the agent did">
                    <ul className="list-disc space-y-0.5 pl-5 text-xs text-gray-600 dark:text-gray-300">
                        {job.notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                </Section>
            )}
        </div>
    );
}

function Profile({ job }) {
    return (
        <div className="space-y-5">
            {job.groups.filter(g => ["organization", "healthcare", "people"].includes(g.key)).map(g => (
                <Section key={g.key} title={g.label}>
                    <FieldRows fields={g.fields} showUnknown={g.key !== "people"} />
                </Section>
            ))}
        </div>
    );
}

function Contacts({ job }) {
    const contact = job.groups.find(g => g.key === "contact");
    return (
        <div className="space-y-3">
            <p className="rounded-md bg-amber-50 p-2 text-[11px] text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                Published on their own pages. That does not prove a number is active or an inbox is read; nothing here is guessed.
            </p>
            {contact && <FieldRows fields={contact.fields} />}
        </div>
    );
}

function Insights({ job }) {
    const ins = job.insights;
    if (!ins) return <p className="text-xs text-gray-500">No sales insights for this run. They need Standard depth and enough verified facts.</p>;
    return (
        <div className="space-y-5">
            {ins.needs?.length > 0 && (
                <Section title="Possible needs (hypotheses to check, not known problems)">
                    <ul className="space-y-1.5">
                        {ins.needs.map((n, i) => (
                            <li key={i} className="text-xs text-gray-800 dark:text-gray-100">
                                <Lightbulb className="mr-1 inline h-3 w-3 text-amber-500" aria-hidden="true" />{n.hypothesis}
                                {n.based_on && <span className="text-gray-500 dark:text-gray-400"> — from: {n.based_on}</span>}
                            </li>
                        ))}
                    </ul>
                </Section>
            )}
            {ins.opportunities?.length > 0 && (
                <Section title="What we could offer">
                    <ul className="space-y-1.5">
                        {ins.opportunities.map((o, i) => (
                            <li key={i} className="text-xs"><span className="font-medium text-gray-900 dark:text-white">{o.offer}</span>
                                {o.why && <span className="text-gray-500 dark:text-gray-400"> — {o.why}</span>}</li>
                        ))}
                    </ul>
                </Section>
            )}
            {ins.talking_points?.length > 0 && (
                <Section title="Talking points">
                    <ul className="list-disc space-y-0.5 pl-5 text-xs text-gray-800 dark:text-gray-100">
                        {ins.talking_points.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                </Section>
            )}
            {ins.questions?.length > 0 && (
                <Section title="Discovery questions">
                    <ol className="list-decimal space-y-0.5 pl-5 text-xs text-gray-800 dark:text-gray-100">
                        {ins.questions.map((q, i) => <li key={i}>{q}</li>)}
                    </ol>
                </Section>
            )}
        </div>
    );
}

/** Field-level CRM suggestions: approve, edit, or reject each; one call applies them all. */
function Updates({ job, canEdit, onReview, busy }) {
    const pending = job.proposals.filter(p => p.status === "pending");
    const decided = job.proposals.filter(p => p.status !== "pending");
    const [choice, setChoice] = useState({});          // id -> 'approve' | 'reject'
    const [edits, setEdits] = useState({});
    const chosen = Object.entries(choice).filter(([, a]) => a);

    const setAll = (action) => setChoice(Object.fromEntries(pending.map(p => [p.id, action])));
    const submit = () => onReview(chosen.map(([id, action]) => ({
        id: Number(id), action,
        ...(action === "approve" && edits[id] != null ? { value: edits[id] } : {}),
    }))).then(ok => { if (ok) { setChoice({}); setEdits({}); } });

    if (!job.proposals.length) return <p className="text-xs text-gray-500">Nothing to change: the research found nothing the CRM lacks.</p>;
    return (
        <div className="space-y-4">
            {pending.length > 0 && (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                            Nothing is saved until you approve it. Phones and emails are added as new contacts, never written over.
                        </p>
                        {canEdit && (
                            <div className="flex gap-2">
                                <button type="button" className="text-xs text-primary hover:underline" onClick={() => setAll("approve")}>Approve all</button>
                                <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setChoice({})}>Clear</button>
                            </div>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-xs">
                            <thead>
                                <tr className="border-b border-gray-200 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:border-gray-700">
                                    <th className="py-1.5 pr-2 font-semibold">CRM field</th>
                                    <th className="py-1.5 pr-2 font-semibold">Now</th>
                                    <th className="py-1.5 pr-2 font-semibold">Researched</th>
                                    <th className="py-1.5 font-semibold">Decision</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pending.map(p => (
                                    <tr key={p.id} className={cn("border-b border-gray-100 align-top dark:border-gray-700/60", p.is_conflict && "bg-amber-50/60 dark:bg-amber-900/10")}>
                                        <td className="py-2 pr-2 font-medium text-gray-800 dark:text-gray-100">
                                            {p.label}
                                            {p.is_conflict && <span className="block text-[10px] font-normal text-amber-700 dark:text-amber-300">Replaces an existing value</span>}
                                        </td>
                                        <td className="py-2 pr-2 text-gray-500 dark:text-gray-400">{p.current_value || <em>empty</em>}</td>
                                        <td className="py-2 pr-2">
                                            {canEdit && choice[p.id] === "approve" ? (
                                                <input aria-label={`Value for ${p.label}`}
                                                    className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs dark:border-gray-600 dark:bg-gray-800"
                                                    value={edits[p.id] ?? p.proposed_value}
                                                    onChange={e => setEdits(x => ({ ...x, [p.id]: e.target.value }))} />
                                            ) : <span className="text-gray-900 dark:text-white">{p.proposed_value}</span>}
                                            {p.source_url && (
                                                <a href={p.source_url} target="_blank" rel="noopener noreferrer" title={p.excerpt}
                                                    className="mt-0.5 flex items-center gap-0.5 text-[10px] text-gray-500 hover:text-primary">
                                                    <ExternalLink className="h-2.5 w-2.5" aria-hidden="true" />
                                                    <span className="truncate max-w-[260px]">{p.excerpt || p.source_url}</span>
                                                </a>
                                            )}
                                        </td>
                                        <td className="py-2">
                                            {canEdit ? (
                                                <div className="flex gap-1" role="group" aria-label={`Decision for ${p.label}`}>
                                                    <button type="button" aria-pressed={choice[p.id] === "approve"}
                                                        onClick={() => setChoice(c => ({ ...c, [p.id]: c[p.id] === "approve" ? null : "approve" }))}
                                                        className={cn("inline-flex h-7 items-center gap-1 rounded border px-2",
                                                            choice[p.id] === "approve" ? "border-green-600 bg-green-600 text-white" : "border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300")}>
                                                        <Check className="h-3 w-3" aria-hidden="true" /> Approve
                                                    </button>
                                                    <button type="button" aria-pressed={choice[p.id] === "reject"}
                                                        onClick={() => setChoice(c => ({ ...c, [p.id]: c[p.id] === "reject" ? null : "reject" }))}
                                                        className={cn("inline-flex h-7 items-center gap-1 rounded border px-2",
                                                            choice[p.id] === "reject" ? "border-gray-700 bg-gray-700 text-white" : "border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300")}>
                                                        <X className="h-3 w-3" aria-hidden="true" /> Reject
                                                    </button>
                                                </div>
                                            ) : <span className="text-gray-400">Needs edit permission</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {canEdit && (
                        <div className="flex justify-end">
                            <button type="button" disabled={!chosen.length || busy} onClick={submit}
                                className="h-8 rounded-md bg-primary px-3 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40">
                                {busy ? "Saving…" : `Save ${chosen.length || ""} decision${chosen.length === 1 ? "" : "s"}`}
                            </button>
                        </div>
                    )}
                </>
            )}
            {decided.length > 0 && (
                <Section title="Already decided">
                    <ul className="space-y-1 text-xs">
                        {decided.map(p => (
                            <li key={p.id} className="text-gray-600 dark:text-gray-300">
                                <span className={p.status === "approved" ? "text-green-700 dark:text-green-400" : "text-gray-500"}>
                                    {p.status === "approved" ? "Approved" : "Rejected"}
                                </span>{" "}
                                {p.label}: {p.status === "approved" ? p.applied_value : p.proposed_value}
                                {p.decided_by && <span className="text-gray-400"> · {p.decided_by}, {p.decided_at}</span>}
                            </li>
                        ))}
                    </ul>
                </Section>
            )}
        </div>
    );
}

function Sources({ job }) {
    const [showRejected, setShowRejected] = useState(false);
    return (
        <div className="space-y-4">
            <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {job.sources.map(s => (
                    <li key={s.id} className="flex flex-wrap items-start gap-2 py-1.5 text-xs">
                        <span className="w-7 shrink-0 font-mono text-[10px] text-gray-500">{s.id}</span>
                        <div className="min-w-0 flex-1">
                            {s.url.startsWith("http") ? (
                                <a href={s.url} target="_blank" rel="noopener noreferrer" className="break-all text-primary hover:underline">{s.title || s.url}</a>
                            ) : <span className="text-gray-800 dark:text-gray-100">{s.title || s.url}</span>}
                            <p className="text-[10px] text-gray-500">
                                {s.kind} · {s.fetched_at}{s.status === "ok" ? ` · ${s.facts} fact${s.facts === 1 ? "" : "s"}` : ` · could not open: ${s.error}`}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
            {job.rejected_claims?.length > 0 && (
                <Section title={`Claims thrown out (${job.rejected_claims.length})`}
                    aside={<button type="button" className="text-[11px] text-primary hover:underline" onClick={() => setShowRejected(s => !s)} aria-expanded={showRejected}>{showRejected ? "Hide" : "Show"}</button>}>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">The AI said these, but the page it cited does not say so. They are never shown as facts.</p>
                    {showRejected && (
                        <ul className="space-y-0.5 text-[11px] text-gray-600 dark:text-gray-300">
                            {job.rejected_claims.map((r, i) => (
                                <li key={i}><span className="font-medium">{r.field}</span>: {r.value} <span className="text-gray-400">— {r.reason}</span></li>
                            ))}
                        </ul>
                    )}
                </Section>
            )}
            {job.prompt_version && <p className="text-[10px] text-gray-400">Model {job.cost?.model || "—"} · prompts {job.prompt_version}</p>}
        </div>
    );
}

function History({ history, currentId }) {
    if (!history?.length) return <p className="text-xs text-gray-500">No earlier research.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-xs">
                <thead>
                    <tr className="border-b border-gray-200 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:border-gray-700">
                        <th className="py-1.5 pr-2 font-semibold">When</th><th className="py-1.5 pr-2 font-semibold">By</th>
                        <th className="py-1.5 pr-2 font-semibold">Result</th><th className="py-1.5 pr-2 font-semibold">Website</th>
                        <th className="py-1.5 font-semibold">CRM updates</th>
                    </tr>
                </thead>
                <tbody>
                    {history.map(h => (
                        <tr key={h.id} className={cn("border-b border-gray-100 dark:border-gray-700/60", h.id === currentId && "font-medium")}>
                            <td className="py-1.5 pr-2">{h.finished_at || h.created_at}</td>
                            <td className="py-1.5 pr-2">{h.requested_by || "—"}</td>
                            <td className="py-1.5 pr-2">{h.status_label}{h.fit_score != null ? ` · fit ${h.fit_score}` : ""}</td>
                            <td className="py-1.5 pr-2 break-all">{h.website || "—"}</td>
                            <td className="py-1.5">{h.approved} approved, {h.rejected} rejected{h.pending_reviews ? `, ${h.pending_reviews} open` : ""}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function CompanyResearchReport({ job, history, view, onView, canEdit, onReview, busy }) {
    const pending = useMemo(() => job.proposals.filter(p => p.status === "pending").length, [job.proposals]);
    return (
        <div className="space-y-4">
            <SummaryTiles job={job} />
            <div role="tablist" aria-label="Research views" className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">
                {VIEWS.map(v => (
                    <button key={v} type="button" role="tab" aria-selected={v === view} onClick={() => onView(v)}
                        className={cn("-mb-px whitespace-nowrap border-b-2 px-3 py-1.5 text-xs font-medium",
                            v === view ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200")}>
                        {v}{v === "Updates" && pending ? ` (${pending})` : ""}
                    </button>
                ))}
            </div>
            <div role="tabpanel" aria-label={view}>
                {view === "Overview" && <Overview job={job} />}
                {view === "Profile" && <Profile job={job} />}
                {view === "Contacts" && <Contacts job={job} />}
                {view === "Sales insights" && <Insights job={job} />}
                {view === "Updates" && <Updates key={job.id} job={job} canEdit={canEdit} onReview={onReview} busy={busy} />}
                {view === "Sources" && <Sources job={job} />}
                {view === "History" && <History history={history} currentId={job.id} />}
            </div>
        </div>
    );
}
